import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Map as MapIcon, Crosshair, KeyRound, Save, Loader2 } from "lucide-react";

const TABS = [
  { k: "system", label: "Système", icon: SlidersHorizontal },
  { k: "map", label: "Préférences carte", icon: MapIcon },
  { k: "tracking", label: "Tracking", icon: Crosshair },
  { k: "password", label: "Modifier le mot de passe", icon: KeyRound },
];

const inputCls = "w-full rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-[#17A2B8] transition-colors";

const Row = ({ label, children, hint }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,240px)_1fr] items-center gap-2 sm:gap-4 py-2 border-b border-border/60">
    <label className="text-sm font-medium text-muted-foreground">{label}{hint && <span className="block text-[11px] text-muted-foreground/70">{hint}</span>}</label>
    <div>{children}</div>
  </div>
);

const Check = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 py-1.5 cursor-pointer text-sm">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid}
      className="w-4 h-4 accent-[#17A2B8]" />
    {label}
  </label>
);

export default function SettingsPage() {
  const { settings, save, loaded } = useSettings();
  const [tab, setTab] = useState("system");
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Resynchronise le formulaire une fois les préférences chargées depuis le serveur.
  useEffect(() => { setForm(settings); }, [settings]);

  const doSave = async () => {
    if (!loaded) return;
    setSaving(true);
    try { await save(form); toast.success("Paramètres enregistrés"); }
    catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  // Mot de passe
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const changePw = async () => {
    if (pw.next.length < 6) return toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
    if (pw.next !== pw.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Mot de passe modifié");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPwSaving(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="settings-page">
        <h1 className="text-2xl font-bold mb-1">Paramètres</h1>
        <p className="text-sm text-muted-foreground mb-5">Vos préférences personnelles (enregistrées sur votre compte).</p>

        <div className="flex flex-wrap gap-1.5 border-b border-border mb-5">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`settings-tab-${t.k}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-md border-b-2 transition-colors ${tab === t.k ? "border-[#17A2B8] text-[#2ec5db]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "system" && (
          <div data-testid="settings-panel-system">
            <Row label="Nom d'affichage">
              <input className={inputCls} data-testid="set-display-name" value={form.display_name || ""} onChange={(e) => set("display_name", e.target.value)} />
            </Row>
            <Row label="Fuseau horaire">
              <select className={inputCls} data-testid="set-timezone" value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
                <option value="Pacific/Noumea">(GMT+11) Nouvelle-Calédonie</option>
                <option value="Pacific/Guadalcanal">(GMT+11) Salomon</option>
                <option value="Pacific/Auckland">(GMT+13) Auckland</option>
                <option value="Pacific/Tahiti">(GMT-10) Tahiti</option>
                <option value="Europe/Paris">(GMT+1) Paris</option>
                <option value="UTC">UTC</option>
              </select>
            </Row>
            <Row label="Langue">
              <select className={inputCls} data-testid="set-language" value={form.language} onChange={(e) => set("language", e.target.value)}>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </Row>
            <Row label="Mesure de distance">
              <select className={inputCls} data-testid="set-distance" value={form.distance_unit} onChange={(e) => set("distance_unit", e.target.value)}>
                <option value="km">Kilomètres</option>
                <option value="mi">Miles</option>
              </select>
            </Row>
            <Row label="Litres ou Gallons">
              <select className={inputCls} data-testid="set-volume" value={form.volume_unit} onChange={(e) => set("volume_unit", e.target.value)}>
                <option value="L">Litres</option>
                <option value="gal">Gallons</option>
              </select>
            </Row>
            <Row label="Vitesse">
              <select className={inputCls} data-testid="set-speed" value={form.speed_unit} onChange={(e) => set("speed_unit", e.target.value)}>
                <option value="kmh">Km/h</option>
                <option value="mph">Mph</option>
              </select>
            </Row>
            <Row label="Économie carburant">
              <select className={inputCls} data-testid="set-fuel-econ" value={form.fuel_econ_unit} onChange={(e) => set("fuel_econ_unit", e.target.value)}>
                <option value="l_100km">L/100km</option>
                <option value="mpg">MPG</option>
              </select>
            </Row>
            <Row label="Format de la date">
              <select className={inputCls} data-testid="set-date-format" value={form.date_format} onChange={(e) => set("date_format", e.target.value)}>
                <option value="dd MMM yyyy">21 Jun 2009</option>
                <option value="dd/MM/yyyy">21/06/2009</option>
                <option value="yyyy-MM-dd">2009-06-21</option>
                <option value="MM/dd/yyyy">06/21/2009</option>
              </select>
            </Row>
            <Row label="Celsius ou Fahrenheit">
              <select className={inputCls} data-testid="set-temp" value={form.temp_unit} onChange={(e) => set("temp_unit", e.target.value)}>
                <option value="C">°C</option>
                <option value="F">°F</option>
              </select>
            </Row>
            <Row label="Messages par page">
              <select className={inputCls} data-testid="set-messages-per-page" value={form.messages_per_page} onChange={(e) => set("messages_per_page", Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </Row>
            <Row label="Supprimer les messages de plus de">
              <select className={inputCls} data-testid="set-purge-days" value={form.purge_days} onChange={(e) => set("purge_days", Number(e.target.value))}>
                <option value={30}>30 jours</option>
                <option value={90}>90 jours</option>
                <option value={180}>180 jours</option>
                <option value={365}>365 jours</option>
                <option value={730}>730 jours</option>
              </select>
            </Row>
            <Row label="Page d'accueil">
              <select className={inputCls} data-testid="set-home-page" value={form.home_page} onChange={(e) => set("home_page", e.target.value)}>
                <option value="/dashboard">Live Tracking</option>
                <option value="/analytique">Tableau de bord</option>
                <option value="/historique">Historique</option>
                <option value="/alertes">Alertes</option>
                <option value="/rapports">Rapports</option>
              </select>
            </Row>
            <Row label="Alerte sonore">
              <Check label="Activer un son à la réception d'une alerte" value={form.sound_alert} onChange={(v) => set("sound_alert", v)} testid="set-sound-alert" />
            </Row>
            <Row label="Notification de message">
              <Check label="Afficher une notification à la réception" value={form.message_notification} onChange={(v) => set("message_notification", v)} testid="set-msg-notif" />
            </Row>
            <Row label="Format du conducteur">
              <select className={inputCls} data-testid="set-driver-format" value={form.driver_name_format} onChange={(e) => set("driver_name_format", e.target.value)}>
                <option value="name">Nom du conducteur</option>
                <option value="badge">Badge / iButton</option>
                <option value="none">Ne pas afficher</option>
              </select>
            </Row>
          </div>
        )}

        {tab === "map" && (
          <div data-testid="settings-panel-map">
            <Row label="Type de carte">
              <select className={inputCls} data-testid="set-map-type" value={form.map_type} onChange={(e) => set("map_type", e.target.value)}>
                <option value="satellite">Satellite</option>
                <option value="osm">OpenStreetMap</option>
                <option value="dark">Sombre</option>
                <option value="light">Clair</option>
                <option value="georep">Géorép (NC)</option>
                <option value="georep_sat">Géorép Ortho (NC)</option>
              </select>
            </Row>
            <Row label="Étiquette dynamique au zoom">
              <Check label="Adapter la taille des étiquettes selon le zoom" value={form.dynamic_label_zoom} onChange={(v) => set("dynamic_label_zoom", v)} testid="set-dyn-label" />
            </Row>
            <Row label="Nom du conducteur sur l'unité">
              <Check label="Afficher le nom du conducteur sous forme d'étiquette d'unité" value={form.driver_label_on_unit} onChange={(v) => set("driver_label_on_unit", v)} testid="set-driver-label-unit" />
            </Row>
            <Row label="Véhicules groupés">
              <Check label="Regrouper les véhicules proches (cluster)" value={form.cluster_vehicles} onChange={(v) => set("cluster_vehicles", v)} testid="set-cluster" />
            </Row>
            <div className="mt-4 mb-2 text-sm font-bold">Éléments affichés sur la carte</div>
            <Row label="Véhicules">
              <div className="flex flex-wrap gap-4">
                <Check label="Afficher" value={form.show_vehicles} onChange={(v) => set("show_vehicles", v)} testid="set-show-vehicles" />
                <Check label="Étiquette (nom)" value={form.vehicle_labels} onChange={(v) => set("vehicle_labels", v)} testid="set-vehicle-labels" />
              </div>
            </Row>
            <Row label="Points d'intérêts">
              <Check label="Afficher" value={form.show_pois} onChange={(v) => set("show_pois", v)} testid="set-show-pois" />
            </Row>
            <Row label="Zone interdite">
              <Check label="Afficher" value={form.show_forbidden_zones} onChange={(v) => set("show_forbidden_zones", v)} testid="set-show-forbidden" />
            </Row>
            <Row label="Géo-clôtures">
              <Check label="Afficher" value={form.show_geofences} onChange={(v) => set("show_geofences", v)} testid="set-show-geofences" />
            </Row>
            <Row label="Zoom d'apparition des POI">
              <input type="number" min={1} max={20} className={inputCls} data-testid="set-poi-zoom" value={form.poi_zoom} onChange={(e) => set("poi_zoom", Number(e.target.value))} />
            </Row>
          </div>
        )}

        {tab === "tracking" && (
          <div data-testid="settings-panel-tracking" className="max-w-xl">
            <p className="text-sm text-muted-foreground mb-3">Informations affichées dans les fiches véhicule / popups de la carte.</p>
            <Check label="Afficher les données de diagnostic (tension, batterie, GSM, CAN)" value={form.track_show_diagnostics} onChange={(v) => set("track_show_diagnostics", v)} testid="set-track-diag" />
            <Check label="Afficher le statut entrée/sortie (IO)" value={form.track_show_io} onChange={(v) => set("track_show_io", v)} testid="set-track-io" />
            <Check label="Afficher le nom du conducteur" value={form.track_show_driver} onChange={(v) => set("track_show_driver", v)} testid="set-track-driver" />
            <Check label="Afficher l'emplacement du véhicule" value={form.track_show_location} onChange={(v) => set("track_show_location", v)} testid="set-track-location" />
            <Check label="Afficher la dernière heure GPS" value={form.track_show_last_gps} onChange={(v) => set("track_show_last_gps", v)} testid="set-track-last-gps" />
            <Check label="Afficher le compteur / heures moteur" value={form.track_show_odometer} onChange={(v) => set("track_show_odometer", v)} testid="set-track-odo" />
            <Check label="Afficher les coordonnées (lat/lon)" value={form.track_show_coords} onChange={(v) => set("track_show_coords", v)} testid="set-track-coords" />
            <Check label="Mettre en évidence le dernier trajet" value={form.track_highlight_last_trip} onChange={(v) => set("track_highlight_last_trip", v)} testid="set-track-highlight" />
          </div>
        )}

        {tab !== "password" && (
          <div className="mt-6 flex justify-end">
            <button onClick={doSave} disabled={saving || !loaded} data-testid="settings-save-btn"
              className="flex items-center gap-2 rounded-md bg-[#F5821F] hover:bg-[#e0741a] text-white font-bold text-sm px-6 py-2.5 transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Sauvegarder
            </button>
          </div>
        )}

        {tab === "password" && (
          <div data-testid="settings-panel-password" className="max-w-md space-y-4">
            <div>
              <label className="text-sm font-medium">Mot de passe actuel</label>
              <input type="password" className={inputCls} data-testid="pw-current" value={pw.current} onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium">Nouveau mot de passe</label>
              <input type="password" className={inputCls} data-testid="pw-new" value={pw.next} onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium">Confirmer le nouveau mot de passe</label>
              <input type="password" className={inputCls} data-testid="pw-confirm" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} />
            </div>
            <button onClick={changePw} disabled={pwSaving} data-testid="pw-save-btn"
              className="flex items-center gap-2 rounded-md bg-[#F5821F] hover:bg-[#e0741a] text-white font-bold text-sm px-6 py-2.5 transition-colors disabled:opacity-60">
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Modifier le mot de passe
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
